'use client';

import type { EventBusListener } from '@hc-frontend/core-events';
import type { CostModel } from '@hc-frontend/core-types';
import { useEventBus } from '@hc-frontend/core-ui';
import {
  buildAdContext,
  trackAdClick as trackAdClickSnowplow,
  trackAdImpression as trackAdImpressionSnowplow,
} from '@hc-frontend/third-party-snowplow';
import { useCallback, useEffect } from 'react';

import type { CompositionRoot } from '../../../config/root/client';
import { useCompositionRoot } from '../../../config/root/client';
import type {
  AdClickEvent,
  AdEventTarget,
  AdImpressionEvent,
} from './ad-events-subscription.types';

function trackAdImpressionWithContext(
  {
    featuredAds,
    contexts = [],
    placement,
    campaignPlacement,
    adsSource,
    sessionId,
    product,
    device,
    stateAcronym,
    zipCode,
    domain,
  }: AdImpressionEvent,
  { trackAdImpression }: Pick<CompositionRoot, 'trackAdImpression'>,
): void {
  trackAdImpression({
    featureAds: {
      result: featuredAds.result,
      id: featuredAds.id,
    },
    productType: product,
    device,
    placement,
    sessionId,
    stateAcronym,
    zipCode,
    domain,
  });

  featuredAds.result.forEach((ad, index) => {
    const impressionPosition = index + 1;

    const adContext = buildAdContext({
      adId: ad.id,
      searchMethod: featuredAds.searchMethod,
      productFit: featuredAds.segmentationFit,
      category: campaignPlacement,
      placement: adsSource,
      impressionPosition,
      groupId: ad.groupId,
      creativeId: ad.creativeId,
      creativeTitle: ad.title,
      advertiserName: ad.provider.name,
      campaignName: ad.campaignName,
      productName: product.toLowerCase(),
    });

    trackAdImpressionSnowplow(
      {
        impressionId: featuredAds.id,
        impressionOrder: impressionPosition.toString(),
        bannerId: ad.creativeId.toString(),
        campaignId: ad.campaignId.toString(),
        advertiserId: ad.provider.id.toString(),
        targetUrl: ad.link,
        costModel: ad.dealType.toLowerCase() as 'cpc' | 'cpa',
        cost: ad.cpc,
      },
      [...contexts, adContext],
    );
  });
}

function trackAdClickWithContext(
  {
    productType,
    featuredAd,
    impressionPosition,
    impressionId,
    device,
    placement,
    sessionId,
    contexts,
    stateAcronym,
    zipCode,
    clickCount,
  }: AdClickEvent,
  {
    trackAdClick,
    trackAdConversion,
  }: Pick<CompositionRoot, 'trackAdClick' | 'trackAdConversion'>,
  source: string,
): void {
  const { link, dealType, cpc, provider, campaignId, id } = featuredAd;

  trackAdClickSnowplow(
    {
      targetUrl: link,
      costModel: dealType.toLowerCase() as Lowercase<CostModel>,
      cost: cpc,
      impressionOrder: impressionPosition.toString(),
      advertiserId: provider.id.toString(),
      campaignId: campaignId.toString(),
      impressionId,
    },
    contexts,
  );

  trackAdClick({
    featureAd: {
      id,
      provider,
      link,
      dealType,
      cpc,
    },
    clickCount,
    impressionId,
    device,
    placement,
    productType,
    sessionId,
    searchAdsId: impressionId,
    blockPosition: impressionPosition,
    pagePosition: impressionPosition,
    stateAcronym,
    zipCode,
  });

  trackAdConversion({
    placement,
    sessionId,
    featureAd: {
      cpc,
      id,
    },
    featureAds: {
      id: impressionId,
    },
  });

  window.dataLayer.push(
    { cpc },
    { event: `fa_conversion_${dealType.toLowerCase()}_click` },
  );

  // Bing - Tracking - TYP
  if (['30', '33', '300', '330', '300D', '330D'].includes(source)) {
    window.uetq.push({
      ec: 'Conversion',
      ea: 'Click',
      el: placement,
      ev: cpc,
      gv: cpc,
    });
  }
}

export function AdEventsSubscription({ source }: { source: string }) {
  const eventBus = useEventBus();
  const { trackAdConversion, trackAdClick, trackAdImpression } =
    useCompositionRoot();

  const handleAdImpression = useCallback<
    EventBusListener<AdImpressionEvent, AdEventTarget>
  >(
    (event) => trackAdImpressionWithContext(event, { trackAdImpression }),
    [trackAdImpression],
  );

  const handleAdClick = useCallback<
    EventBusListener<AdClickEvent, AdEventTarget>
  >(
    (event) =>
      trackAdClickWithContext(
        event,
        { trackAdConversion, trackAdClick },
        source,
      ),
    [trackAdClick, trackAdConversion, source],
  );

  useEffect(() => {
    eventBus.subscribe('core.ui.ad.impression', handleAdImpression);
    eventBus.subscribe('core.ui.ad.click', handleAdClick);

    return () => {
      eventBus.unsubscribe('core.ui.ad.impression', handleAdImpression);
      eventBus.unsubscribe('core.ui.ad.click', handleAdClick);
    };
  }, [handleAdClick, handleAdImpression, eventBus]);

  return null;
}
